<!-- Timeline.vue -->
<script lang="ts" setup>
    import '../../css/choice.css';
    import { computed, reactive, onMounted, ref, watch, nextTick, onBeforeMount } from 'vue';
    import { ITimelineSetup, ITimelineVital, ITimelineProgressNote, ITimelineMail, ITimelineMedication, ITimelineImmunization, ITimelineObstetric, 
             ITimelineOrderResult, ITimelineDocument, ITimelinePatientReported, ITimelineTelemedicine, ITimelineAuthorizationItems, ITimelineManualAuthorization, ITimelineElectronicAuthorization, ITimelineSectionSetupItem,
             TimelineSection, ITimelineSectionClick, IEncounterDate } from "../lib/Models/EMR/TimelineModel";
    import { timeline } from '../lib/ChoiceApi';
    import { getShortDate, NoTzDate } from '../lib/DateUtils';
    import { ModalSize, TextDisplayStyle } from '../Enums/UiEnums';
    import { MessageText } from '../LibMain';
    import { initPinia } from '../lib/PiniaMgr';
    import { useUserSettingsStore, EmrTimelineSetting } from '../lib/Stores/UserSettingsStore'; 
    import HtmlImmunizations  from '../components/HtmlImmunizations.vue';
    import HtmlMedications  from '../components/HtmlMedications.vue';
    import HtmlObstetrics  from '../components/HtmlObstetrics.vue';
    import HtmlTelemedicine  from '../components/HtmlTelemedicine.vue';
    import AuthorizationItems from '../ChoiceScreens/Timeline/AuthorizationItems.vue'
    import ModalDialog from './ModalDialog.vue';
    import CmmTaskListItem from '../ChoiceScreens/Prescriptions/CoverMyMeds/ePA/CmmTaskListItem.vue';
    import { UserSettingGroup } from '../Enums/ChoiceEnums';

    const props = withDefaults(defineProps<
    {
        patientId: number,
        appointmentId: number,
        encounterDate: NoTzDate,
        reconcileCount: number,
        reconcileEnabled: boolean,
        sections: ITimelineSectionSetupItem[]
    }>(), {});    

    const emit = defineEmits<{
        (e: "sectionHeaderClick", strValue: TimelineSection): void,
        (e: "sectionItemClick", strValue: ITimelineSectionClick): void,
        (e: "openReconciliation"): void,
        (e: "openVitals"): void
    }>();
    defineExpose({loadTimeline, clearTimeline, reloadSections});

    interface IBaseTimeline {
        sectionList?: ITimelineSectionSetupItem[],
        setup?: ITimelineSetup,
        vitalsList?: ITimelineVital[],
        progressNotesList?: ITimelineProgressNote[],
        mailList?: ITimelineMail[],
        medicationList?: ITimelineMedication[],
        immunizationList?: ITimelineImmunization[],
        obstetricsList?: ITimelineObstetric[],
        orderResultList?: ITimelineOrderResult[],
        documentList?: ITimelineDocument[],
        patientReportedList?: ITimelinePatientReported[],
        telemedicineList?: ITimelineTelemedicine[],
        manualAuthorizationList?: ITimelineManualAuthorization[],
        electronicAuthorizationList?: ITimelineElectronicAuthorization[],
        encounterList: string[],
        gridWidth: number,
        patientId?: number,
        appointmentId?: number,
        encounterDate: NoTzDate,
        reconcileCount: number,
        reconcileEnabled: boolean,
        loading: boolean,
        showAuthorizationItems?: boolean,
        showAuthorizationItemsDate?: NoTzDate,
        showelectronicAuthorization?: boolean,
        electronicAuthorizationToShow?: ITimelineElectronicAuthorization
    }

    const formData = reactive<IBaseTimeline>({
            encounterList: [], 
            gridWidth: 200, 
            encounterDate: "", 
            reconcileCount: 0, 
            reconcileEnabled: false, 
            loading: false
        });
    const gridTimeline = ref<HTMLInputElement>();
    const immunizationDisplay = ref();
    const medicationDisplay = ref();
    const obstetricDisplay = ref();
    const telemedicineDisplay = ref();

    const userSettingsStore = useUserSettingsStore(initPinia());    

    onBeforeMount(async () => {
        // Load timeline user settings so we can use later
        await userSettingsStore.loadGroupSettings(UserSettingGroup.EMRTimelineSettings);
    });

    onMounted(async () => {
        formData.patientId = props.patientId;
        formData.appointmentId = props.appointmentId;
        formData.encounterDate = props.encounterDate;
        formData.sectionList = [...props.sections];
        formData.sectionList.sort((a,b) => a.sort_order - b.sort_order);
        formData.reconcileCount = props.reconcileCount;
        formData.reconcileEnabled = props.reconcileEnabled;
        refreshTimeline();
        window.addEventListener('resize', () => {
            Resize();
        })
    });

    watch(() => props.reconcileCount, (newValue, oldValue) => {
        formData.reconcileCount = newValue;
    });
    watch(() => props.reconcileEnabled, (newValue, oldValue) => {
        formData.reconcileEnabled = newValue;
    });

    function loadTimeline(patientId: number, appointmentId: number, encounterDate: NoTzDate) {
        clearTimeline();
        formData.patientId = patientId;
        formData.appointmentId = appointmentId;
        formData.encounterDate = encounterDate;
        refreshTimeline();
    }

    function clearTimeline() {
        formData.patientId = undefined;
        formData.appointmentId = undefined;
        formData.encounterDate = "";
        formData.reconcileCount = -1;
        formData.vitalsList = [];
        formData.progressNotesList = [];
        formData.mailList = [];
        formData.medicationList = [];
        formData.immunizationList = [];
        formData.obstetricsList = [];
        formData.orderResultList = [];
        formData.documentList = [];
        formData.patientReportedList = [];
        formData.telemedicineList = [];
        formData.manualAuthorizationList = [];
        formData.electronicAuthorizationList = [];
        formData.encounterList = [];
        formData.encounterList.push(formData.setup?.today ?? "");
    }

    function reloadSections(sectionList: ITimelineSectionSetupItem[]) {
        formData.sectionList = [...sectionList];
        formData.sectionList.sort((a,b) => a.sort_order - b.sort_order);
    }

    const displayReconcileNotificationExists = computed<boolean>(() => (formData.reconcileCount > 0 && formData.reconcileEnabled) ? true : false);

    function Resize() {
        formData.gridWidth = window.innerWidth -  210;
    }

    async function refreshTimeline() {

        const setupPromises: Promise<unknown>[] = [];
        setupPromises.push(userSettingsStore.loadGroupSettings(UserSettingGroup.EMRTimelineSettings));
        setupPromises.push(timeline.setup().then(resp => formData.setup = resp));
        await Promise.all(setupPromises);

        if (!formData.patientId) {
            clearTimeline();
            return;
        }
        formData.loading = true;

        // Decide what we need to load from the API
        const timelinePromises = [];

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.VitalsEnabled))
            timelinePromises.push(timeline.vitals(formData.patientId).then(resp => formData.vitalsList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.ProgressNotesEnabled))
            timelinePromises.push(timeline.progressNotes(formData.patientId).then(resp => formData.progressNotesList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.MailEnabled))
            timelinePromises.push(timeline.mail(formData.patientId).then(resp => formData.mailList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.MedicationsEnabled))
            timelinePromises.push(timeline.medications(formData.patientId).then(resp => formData.medicationList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.ImmunizationsEnabled))
            timelinePromises.push(timeline.immunizations(formData.patientId).then(resp => formData.immunizationList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.ObstetricsEnabled))
            timelinePromises.push(timeline.obstetrics(formData.patientId).then(resp => formData.obstetricsList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.OrderResultsEnabled))
            timelinePromises.push(timeline.orderResults(formData.patientId).then(resp => formData.orderResultList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.DocumentsEnabled))
            timelinePromises.push(timeline.documents(formData.patientId).then(resp => formData.documentList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.PatientReportedEnabled))
            timelinePromises.push(timeline.patientReported(formData.patientId).then(resp => formData.patientReportedList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.TelemedicineEnabled))
            timelinePromises.push(timeline.telemedicine(formData.patientId).then(resp => formData.telemedicineList = resp));

        if ("Y" === await userSettingsStore.getEmrTimelineSettingString(EmrTimelineSetting.PriorAuthEnabled))
        {
            timelinePromises.push(timeline.authorizations(formData.patientId).then(resp => {
                formData.manualAuthorizationList = resp?.manualAuthItems;
                formData.electronicAuthorizationList = resp?.electronicAuthItems
            }));
        }

        await Promise.all(timelinePromises);

        buildEncounterList();
        scrollToRight();
        formData.loading = false;
    }

    function buildEncounterList() {
        buildEncounterListSection(formData.vitalsList);
        buildEncounterListSection(formData.progressNotesList);
        buildEncounterListSection(formData.mailList);
        buildEncounterListSection(formData.immunizationList);
        buildEncounterListSection(formData.obstetricsList);
        buildEncounterListSection(formData.orderResultList);
        buildEncounterListSection(formData.documentList);
        buildEncounterListSection(formData.patientReportedList);
        buildEncounterListSection(formData.telemedicineList);
        buildEncounterListSection(formData.manualAuthorizationList);
        buildEncounterListSection(formData.electronicAuthorizationList);
        if (formData.medicationList) {
            const med_list_start = formData.medicationList.filter(item => (item.startDate ?? "") != "");
            const med_list_stop = formData.medicationList.filter(item => (item.stopDate ?? "") != "");
            formData.encounterList = formData.encounterList.concat(med_list_start.map(item => item.startDate ?? ""));
            formData.encounterList = formData.encounterList.concat(med_list_stop.map(item => item.stopDate ?? ""));
        }
        formData.encounterList = [... new Set(formData.encounterList)]; // Get the unique list of values
        formData.encounterList.sort((date1, date2) => new Date(date1).getTime() - new Date(date2).getTime());
    }

    function buildEncounterListSection(list?: IEncounterDate[]) {
        if (list?.length)
            formData.encounterList = formData.encounterList.concat(list.map(item => item.encounterDate));
    }

    function scrollToRight() {
        nextTick(() => { 
            if (gridTimeline.value)
                gridTimeline.value.scrollTo({left: gridTimeline.value.scrollWidth});
        });        
    }

    const vitalsExist = computed<boolean>(() => {
        if (!formData.vitalsList)
            return false;
        return formData.vitalsList.length > 0 ? true : false;
    });

    function getShortDateTimeline(date: NoTzDate): string {
        return getShortDate(date, formData.setup?.today);
    }

    function onSectionTitleClick(sectionIndex: number) {
        if (!formData.sectionList)
            return;
        emit("sectionHeaderClick", formData.sectionList[sectionIndex].id);
    }

    function onSectionClick(section: ITimelineSectionSetupItem, date: NoTzDate) {
        switch (section.id) {
            case TimelineSection.Vitals:
            case TimelineSection.OrderResults:
            case TimelineSection.PatientReported:
                return sectionCellClickSimple(date, section.id);
            case TimelineSection.Mail:
                return sectionCellClickMail(date);
            case TimelineSection.Medications:
                return sectionCellClickMedications(date);
            case TimelineSection.ProgressNotes:
                return sectionCellClickProgressNotes(date);
            case TimelineSection.Immunizations:
                return sectionCellClickImmunizations(date);
            case TimelineSection.Obstetrics:
                return sectionCellClickObstetrics(date);
            case TimelineSection.Documents:
                return sectionCellClickDocuments(date);
            case TimelineSection.Telemedicine:
                return sectionCellClickTelemedicine(date);
            case TimelineSection.Authorizations:
            {
                const electronicAuthorizationItemsForDate = formData.electronicAuthorizationList?.filter(item => item.encounterDate === date);
                // No electronic auth?
                if (!electronicAuthorizationItemsForDate?.length)
                {
                    return sectionCellClickSimple(date, section.id);
                }

                // No manual auth?
                if (1 === electronicAuthorizationItemsForDate?.length)
                {
                    formData.electronicAuthorizationToShow = electronicAuthorizationItemsForDate[0];
                    formData.showelectronicAuthorization = true;
                    return;
                }

                formData.showAuthorizationItemsDate = date;
                formData.showAuthorizationItems = true;
            }
        }
    }

    function sectionCellClickMail(date: NoTzDate) {
        if (!formData.mailList)
            return;
        let list = formData.mailList.filter(item => item.encounterDate === date);
        list.sort((a,b) => a.mailId - b.mailId);
        if (list.length == 0)
            return;
        const timelineSectionClick: ITimelineSectionClick = {
            id: TimelineSection.Mail,
            sectionId: list[0].mailId,
            date: date
        }
        emit("sectionItemClick", timelineSectionClick);
    }

    function sectionCellClickMedications(date: NoTzDate ) {
        if (!formData.medicationList)
            return;
        medicationDisplay.value.buildDisplay(formData.medicationList, date);
    }

    function sectionCellClickProgressNotes(date: NoTzDate) {
        if (!formData.progressNotesList)
            return;
        let list = formData.progressNotesList.filter(item => item.encounterDate === date);
        list.sort((a,b) => a.soapNoteId - b.soapNoteId);
        let data: string[] = [];
        list.forEach(function(item) {
            if (item.nursesNoteLetterId) 
            {
                data.push(item.nursesNoteLetterId.toString());
                data.push(`Nurses Note^<patient_id>${formData.patientId}</patient_id><encounter_date>${formData.encounterDate}</encounter_date><appointment_id>${formData.appointmentId ?? -1}</appointment_id><letter_template_id>${item.nursesNoteLetterId}</letter_template_id>`);
            } 
            else 
            {
                data.push(item.soapNoteId.toString());
                data.push(`${item.encounterDate} ${item.soapNoteTypeDescription}` + (item.signOffName == "" ? "" : ` - ${item.signOffName}`));
            }
        });

        const timelineSectionClick: ITimelineSectionClick =  {
            id: TimelineSection.ProgressNotes,
            date: date,
            data: data.join("~")   
        }
        emit("sectionItemClick", timelineSectionClick);
    }

    function sectionCellClickSimple(date: NoTzDate, section: TimelineSection) {
        const timelineSectionClick: ITimelineSectionClick =  {
            id: section,
            date: date
        }
        formData.showAuthorizationItemsDate = undefined;
        emit("sectionItemClick", timelineSectionClick);        
    }

    function sectionCellClickImmunizations(date: NoTzDate) {
        if (!formData.immunizationList)
            return;
        immunizationDisplay.value.buildDisplay(formData.immunizationList, date);
    }

    function displayBuiltSection(timelineSectionClick: ITimelineSectionClick) {
        emit("sectionItemClick", timelineSectionClick);        
    }

    function sectionCellClickObstetrics(date: NoTzDate) {
        if (!formData.obstetricsList)
            return;
        obstetricDisplay.value.buildDisplay(formData.obstetricsList, date);
    }

    function sectionCellClickDocuments(date: NoTzDate) {
        if (!formData.documentList)
            return;
        const list = formData.documentList.filter(item => item.encounterDate === date);
        if (list.length == 0)
            return;
        let data = `<image_id>${list[0].imageId}</image_id>`;
        if (list[0].documentId)
            data += `<document_id>${list[0].documentId}</document_id>`;
        const timelineSectionClick: ITimelineSectionClick = {
            id: TimelineSection.Documents,
            date: date,
            data: data
        }
        emit("sectionItemClick", timelineSectionClick);        
    }

    function sectionCellClickTelemedicine(date: NoTzDate) {
        if (!formData.telemedicineList)
            return;
        telemedicineDisplay.value.buildDisplay(formData.telemedicineList, date);
    }

    function vitalsTooltip(date: NoTzDate) {
        if (!formData.vitalsList)
            return;
        const itemList = formData.vitalsList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            if (item.height) detail.push(`height: ${item.height}`);
            if (item.weight) detail.push(`weight: ${item.weight}`);
            if (item.systolic && item.diastolic) detail.push(`BP: ${item.systolic}/${item.diastolic}`);
            if (item.pulse) detail.push(`Pulse: ${item.pulse}`);
            if (item.bmi) detail.push(`BMI: ${item.bmi}`);
            if (item.bsa) detail.push(`BSA: ${item.bsa}`);
            detail.push("");
        }
        return detail.join("\r\n");
    }

    function progressNotesTooltip(date: NoTzDate) {
        if (!formData.progressNotesList)
            return;
        const itemList = formData.progressNotesList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            if (item.soapNoteTypeDescription && item.signOffName) 
                detail.push(`${item.soapNoteTypeDescription}: ${item.signOffName}`);
            else if (item.soapNoteTypeDescription)
                detail.push(`${item.soapNoteTypeDescription}`);
            else if (item.signOffName)
                detail.push(`${item.signOffName}`);
        }
        return detail.join("\r\n");
    }

    function mailTooltip(date: NoTzDate) {
        if (!formData.mailList)
            return;
        const itemList = formData.mailList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            if (item.fromUser) detail.push(`From,: ${item.fromUser}`);
            if (item.toUser) detail.push(`To: ${item.toUser}`);
            if (item.subject) detail.push(`Subject: ${item.subject}`);
            if (item.message) detail.push(`${item.message}`);
            detail.push("");
        }
        return detail.join("\r\n");
    }


    function medicationTooltip(date: NoTzDate) {
        if (!formData.medicationList)
            return;
        const itemList = formData.medicationList.filter(item => (item.startDate ?? "") == date || (item.stopDate ?? "") === date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            if (item.shortName) detail.push(`${item.shortName}`);
        }
        return detail.join("\r\n");
    }

    function immunizationTooltip(date: NoTzDate) {
        if (!formData.immunizationList)
            return;
        const itemList = formData.immunizationList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            if (item.immunizationName) detail.push(`${item.immunizationName}`);
        }
        return detail.join("\r\n");
    }

    function obstetricTooltip(date: NoTzDate) {
        if (!formData.obstetricsList)
            return;
        const itemList = formData.obstetricsList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            if (item.edd) detail.push(`EDD: ${item.edd}`);
            if (item.weeksGest) detail.push(`Weeks Gest: ${item.weeksGest}`);
            if (item.fundalHeight) detail.push(`Fundal Height: ${item.fundalHeight}`);
        }
        return detail.join("\r\n");
    }

    function orderResultTooltip(date: NoTzDate) {
        if (!formData.orderResultList)
            return;
        const itemList = formData.orderResultList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        let countOrdersDisplayed = 0;
        for (const item of itemList) {
            if (item.orderItemDescription) detail.push(`${item.orderItemDescription}`);
            countOrdersDisplayed++;
            if (countOrdersDisplayed == item.maxOrderDisplayLimit) {
                detail.push("...");
                break;
            }
        }
        return detail.join("\r\n");
    }

    function documentTooltip(date: NoTzDate) {
        if (!formData.documentList)
            return;
        const itemList = formData.documentList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            detail.push(`${item.imagePath}: ${item.imageName}${documentNeedsReview(item)}`);
        }
        return detail.join("\r\n");
    }

    function documentNeedsReview(item: ITimelineDocument) {
        return item.reviewNeeded ? " (Review)" : "";
    }


    function patientReportedTooltip(date: NoTzDate) {
        if (!formData.patientReportedList)
            return;
        const itemList = formData.patientReportedList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            detail.push(`${item.type}`);
        }
        return detail.join("\r\n");
    }

    function telemedicineTooltip(date: NoTzDate) {
        if (!formData.telemedicineList)
            return;
        const itemList = formData.telemedicineList.filter(item => item.encounterDate == date);
        let detail = [];
        detail.push(date);
        for (const item of itemList) {
            detail.push(`${item.startSessionDate} - ${item.endSessionDate}`);
        }
        return detail.join("\r\n");
    }

    function authorizationTooltip(date: NoTzDate) {
        const manualDetail = formData.manualAuthorizationList
            ?.filter(item => item.encounterDate == date)
            .map(item => `Manual: ${item.reason}`);

        const electronicDetail = formData.electronicAuthorizationList
            ?.filter(item => item.encounterDate == date)
            .map(item => `ePA: ${item.ePATaskStatus}: ${item.drugShortName}`);

        let detail = [];
        detail.push(date);
        if (manualDetail?.length)
            detail.push(...manualDetail);
        if (electronicDetail?.length)
            detail.push(...electronicDetail);

        return detail.join("\r\n");
    }

    function sectionCellIcon(section: ITimelineSectionSetupItem, date: NoTzDate) {
        switch (section.id) {
            case TimelineSection.Vitals:
                return sectionCell(formData.vitalsList, date, "fa-heart-pulse", "timeline-vitals-icon-color");
            case TimelineSection.ProgressNotes:
                return sectionCell(formData.progressNotesList, date, "fa-file-lines", "timeline-progress-notes-icon-color");
            case TimelineSection.Mail:
                return sectionCell(formData.mailList, date, "fa-envelope", "timeline-mail-icon-color");
            case TimelineSection.Medications:
                return medicationCell(formData.medicationList, date, "fa-pills timeline-medications-icon-color");
            case TimelineSection.Immunizations:
                return sectionCell(formData.immunizationList, date, "fa-syringe", "timeline-immunizations-icon-color");
            case TimelineSection.Obstetrics:
                return sectionCell(formData.obstetricsList, date, "fa-baby", "timeline-obstetrics-icon-color");
            case TimelineSection.OrderResults:
                return sectionCell(formData.orderResultList, date, "fa-flask", "timeline-order-results-icon-color");
            case TimelineSection.Documents:
                return documentCell(formData.documentList, date);
            case TimelineSection.PatientReported:
                return sectionCell(formData.patientReportedList, date, "fa-right-left", "timeline-patient-reported-icon-color");
            case TimelineSection.Telemedicine:
                return sectionCell(formData.telemedicineList, date, "fa-video", "timeline-telemedicine-icon-color");
            case TimelineSection.Authorizations:
                {
                    const authItems = [];
                    authItems.push(...(formData.manualAuthorizationList ?? []));
                    authItems.push(...(formData.electronicAuthorizationList ?? []));
                    return sectionCell(authItems, date, "fa-circle-check", "timeline-authorizations-icon-color");
                }
        }
    }

    function sectionCell(list: IEncounterDate[]|undefined, date: NoTzDate, iconClass: string, colorClass: string) {
        return (list?.findIndex(item => item.encounterDate === date) ?? -1) >= 0 ? `fa-solid ${iconClass} ${colorClass} pointer` : "";
    }

    function medicationCell(list: ITimelineMedication[] | undefined, date: NoTzDate, icon: string) {
        return (list?.findIndex(item => (item.startDate ?? "") === date || (item.stopDate ?? "") === date) ?? -1) >= 0 ? `fa-solid ${icon} pointer` : "";
    }

    function documentCell(list: ITimelineDocument[] | undefined, date: NoTzDate) {
        const encounterList = list?.filter(item => item.encounterDate === date);
        if (!encounterList)
            return;
        if (encounterList.length == 0)
            return "";
        if (encounterList.length == 1) {
            return (encounterList[0].fontStyle ?? "") != "" ? `${encounterList[0].fontStyle} pointer timeline-documents-icon-color` : "fa-solid fa-image pointer timeline-documents-icon-color";
        }
        const fontList = encounterList.map(item => item.fontStyle);
        const fontUnique = [... new Set(fontList)];
        return (fontUnique.length == 1 && fontUnique[0] != null) ? `${fontUnique[0]} pointer timeline-documents-icon-color` : "fa-solid fa-images pointer timeline-documents-icon-color";
    }

    function sectionCellReviewIcon(section: ITimelineSectionSetupItem, date: NoTzDate) {
        if (!formData.sectionList)
            return "review_icon_hide";
        if (section.id != TimelineSection.Documents)
            return "review_icon_hide";
        return (formData.documentList?.findIndex(item => (item.encounterDate === date && item.reviewNeeded)) ?? -1) >= 0 ? `fa-solid fa-circle timeline-vitals-icon-color` : "";
    }

    function sectionCellStyle(section: ITimelineSectionSetupItem, date: NoTzDate) {
        if (section.id != TimelineSection.Documents)
            return "";
        const encounterList = formData.documentList?.filter(item => item.encounterDate === date);
        if (!encounterList)
            return "";
        if (encounterList.length == 0)
            return "";
        const colorList = encounterList.map(item => item.fontColor);
        const colorUnique = [... new Set(colorList)];
        return (colorUnique.length == 1 && colorUnique[0] != null) ? `color: ${colorUnique[0]}` : "";
    }

    function sectionCellTooltip(section: ITimelineSectionSetupItem, date: NoTzDate) {
        switch (section.id) {
            case TimelineSection.Vitals:
                return vitalsTooltip(date);
            case TimelineSection.ProgressNotes:
                return progressNotesTooltip(date);
            case TimelineSection.Mail:
                return mailTooltip(date);
            case TimelineSection.Medications:
                return medicationTooltip(date);
            case TimelineSection.Immunizations:
                return immunizationTooltip(date);
            case TimelineSection.Obstetrics:
                return obstetricTooltip(date);
            case TimelineSection.OrderResults:
                return orderResultTooltip(date);
            case TimelineSection.Documents:
                return documentTooltip(date);
            case TimelineSection.PatientReported:
                return patientReportedTooltip(date);
            case TimelineSection.Telemedicine:
                return telemedicineTooltip(date);
            case TimelineSection.Authorizations:
                return authorizationTooltip(date);
        }
    }

    function timelineCellStyle(index: number) {
        return index === formData.encounterList.length-1 ? "font-size: 1.082rem; padding: 0.0rem 0.25rem; background-color: #cee2f9; opacity: 0.75" : "font-size: 1.082rem; padding: 0.0rem 0.25rem;";
    }

    function onOpenReconciliation() {
        emit("openReconciliation");
    }
    function onOpenVitals() {
        emit("openVitals");
    }

    function onClickViewManualAuthInfo()
    {
        if (!formData.showAuthorizationItemsDate)
        {
            alert("formData.showAuthorizationItemsDate is invalid");
            return;
        }

        return sectionCellClickSimple(formData.showAuthorizationItemsDate, TimelineSection.Authorizations);
    }

    const currentManualAuthorizationItems = computed(() => formData.manualAuthorizationList?.filter(item => item.encounterDate === formData.showAuthorizationItemsDate));
    const currentElectronicAuthorizationItems = computed(() => formData.electronicAuthorizationList?.filter(item => item.encounterDate === formData.showAuthorizationItemsDate));

    function closeAuthorizationItemsPanel()
    {
        formData.showAuthorizationItems = undefined; 
        formData.showAuthorizationItemsDate = undefined; 
        scrollToRight();        
    }
</script>

<style>
    .timeline-vitals-icon-color {
        color: #AD343E
    }
    .timeline-medications-icon-color {
        color: #4357AD
    }
    .timeline-progress-notes-icon-color {
        color: #2F4F4F
    }
    .timeline-order-results-icon-color {
        color: #00A36C
    }
    .timeline-patient-reported-icon-color {
        color: #4357AD
    }
    .timeline-mail-icon-color {
        color: #F4C95D
    }
    .timeline-authorizations-icon-color {
        color: #00A36C
    }
    .timeline-documents-icon-color {
        color: #2F4F4F
    }
    .timeline-telemedicine-icon-color {
        color: #4357AD
    }
    .timeline-immunizations-icon-color {
        color: #AD343E
    }
    .timeline-obstetrics-icon-color {
        color: black;
    }
    .timeline-action-button {
        padding-top: 0rem;
        padding-bottom: 0rem;
        font-size: 1.1rem;
    }
    .review_icon_display {
        display: block;
    }
    .review_icon_hide {
        display: none;
    }
</style>

<template>
    <div class="row p-2"> 
        <div class="col-2">
            <table class="table table-bordered table-striped text-center text-truncate label-isalus-small padding-25" style="margin-bottom: 0.25rem">
                <tbody>
                    <tr><th>Section</th></tr>
                    <tr v-for="(section, index) in formData.sectionList"><td style="padding: 0.315rem 0.25rem;" class="pointer" v-on:click="onSectionTitleClick(index)">{{ section.name }}</td></tr>
                </tbody>
            </table>
            <div class="p-0">
                <div class="btn-group" role="group">
                    <button v-if="formData.reconcileEnabled" type="button" class="btn btn-outline-secondary pt-0 mb-0 timeline-action-button" title="Reconciliation of allergies, medications and problems" v-on:click="onOpenReconciliation()">
                        <i class="fa-solid fa-clipboard-list timeline-mail-icon-color"></i>
                        <i v-if="displayReconcileNotificationExists" class="fa-solid fa-circle timeline-vitals-icon-color" style="font-size: 0.50rem; vertical-align:top; margin-top: 0.25rem"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary pt-0 mb-0 timeline-action-button" title="Open the vitals summary screen for the patient" v-on:click="onOpenVitals()">
                        <i class="fa-solid fa-heart-pulse timeline-vitals-icon-color"></i>
                    </button>
                    <button type="button" v-if="formData.loading" class="btn btn-outline-secondary pt-0 mb-0 timeline-action-button" title="Loading timeline" disabled>
                        <i class="fas fa-spinner fa-pulse text-primary"></i>
                    </button>
                </div>
            </div>            
        </div>
        <div class="col-10">
            <div ref="gridTimeline" style="overflow-x: scroll" :width="formData.gridWidth">
                <table class="table table-bordered table-striped text-center label-isalus-small padding-25">
                    <tbody>
                        <tr>
                            <th v-for="item in formData.encounterList">{{ getShortDateTimeline(item) }}</th>
                        </tr>
                        <tr v-for="(section,index) in formData.sectionList">
                            <td v-for="(item,cellIndex) in formData.encounterList" :style="timelineCellStyle(cellIndex)">
                                <i :class="sectionCellIcon(section, item)" 
                                        :title="sectionCellTooltip(section, item)" 
                                        v-on:click="onSectionClick(section, item)" 
                                        :style="sectionCellStyle(section, item)"></i>

                                <i :class="sectionCellReviewIcon(section, item)" style="font-size: 0.50rem; vertical-align:top; margin-top: 0.25rem"></i>
                                <br/>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
    <div style="display: none">
        <HtmlImmunizations @displayBuiltSection="displayBuiltSection" ref="immunizationDisplay" />
    </div>
    <div style="display: none">
        <HtmlMedications @displayBuiltSection="displayBuiltSection" ref="medicationDisplay" />
    </div>
    <div style="display: none">
        <HtmlObstetrics @displayBuiltSection="displayBuiltSection" ref="obstetricDisplay" />
    </div>
    <div style="display: none">
        <HtmlTelemedicine @displayBuiltSection="displayBuiltSection" ref="telemedicineDisplay" />
    </div>

    <Teleport :to="'body'">
        <ModalDialog v-model:show="formData.showAuthorizationItems" :size="ModalSize.XL">
            <template #title>
                Authorizations 
                <small>
                    <MessageText :displayStyle="TextDisplayStyle.Muted">
                        Encounter: {{formData.showAuthorizationItemsDate}}
                    </MessageText>
                </small>
            </template>

            <AuthorizationItems 
                v-if="formData.showAuthorizationItemsDate"
                :manualAuthorizationItems="currentManualAuthorizationItems" 
                :electronicAuthorizationItems="currentElectronicAuthorizationItems" 
                :itemsDate="formData.showAuthorizationItemsDate"
                @showManual="(date) => sectionCellClickSimple(date, TimelineSection.Authorizations)" 
                @showElectronic="item => {formData.showAuthorizationItems = undefined; formData.electronicAuthorizationToShow = item; formData.showelectronicAuthorization = true}" />
        </ModalDialog>

        <ModalDialog v-model:show="formData.showelectronicAuthorization" @hidden="() => formData.electronicAuthorizationToShow = undefined">
            <template #title>Electronic Authorization</template>

            <CmmTaskListItem 
                v-if="formData.electronicAuthorizationToShow && formData.showelectronicAuthorization"
                :key="`${formData.electronicAuthorizationToShow.cmmEpaCallbackId}:${formData.electronicAuthorizationToShow.cmmEpaSendId}`"
                :taskItemIdType="formData.electronicAuthorizationToShow.taskItemIdType"
                :cmmEpaCallbackId="formData.electronicAuthorizationToShow.cmmEpaCallbackId"
                :cmmEpaSendId="formData.electronicAuthorizationToShow.cmmEpaSendId"
                :hideFooterBar="true"
                :hideQuestionnaire="true"> 
            </CmmTaskListItem>
        </ModalDialog>
    </Teleport>


</template>